/**
 * VisionForge chrome — Forge Suite product face.
 * Live · Ask · HF models · Cloudflare · CivitAI · Forge endpoints · Screen
 */

import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  PiCircleNotchBold,
  PiCloudBold,
  PiCpuBold,
  PiEyeBold,
  PiGoogleLogoBold,
  PiHardDrivesBold,
  PiSignOutBold,
  PiWalletBold,
  PiWebcamBold,
  PiImageBold,
  PiMonitorBold,
} from "react-icons/pi";
import { pickEngineFromRuntime, type ConnectionTab } from "@forgesuite/gpu-browser";
import { useCatalogRuntime } from "../lib/useCatalogRuntime";

import { BackgroundFx } from "./BackgroundFx";
import { GpuBrowserMount } from "./GpuBrowserMount";
import {
  consumeBinderQuery,
  isCatalogShell,
  loadEndpoint,
  loadShellMode,
  saveEndpoint,
  saveShellMode,
  type ShellMode,
  type VisionEndpoint,
} from "../lib/endpoint";
import { VISIONFORGE_HF_BUCKET } from "../lib/hfBucket";
import {
  loadSavedSession,
  stopSuiteSession,
  type InferenceSession,
} from "../lib/suiteInference";
import {
  applyTokensFromHash,
  clearSession,
  fetchMe,
  isLoggedIn,
  startGoogleLoginWeb,
} from "../suite/auth";
import { SUITE_MEMBER_PRICE_USD } from "../suite/access";
import type { SuiteUser, WalletSnapshot } from "../suite/types";
import { fetchWallet, startCheckout, subscribeMember } from "../suite/wallet";
import { getStoredThemeId, selectTheme } from "../themes/applyTheme";
import { BG_FX_OPTIONS, getBgFxId, setBgFxId } from "../themes/bgFx";
import { listThemes } from "../themes/registry";
import { listPickerMotions, listPickerThemes, SUITE_SKINS_EVENT } from "../themes/suiteBridge";
import type { ThemeId } from "../themes/types";
import { getEngine, listEngines } from "../engines/registry";
import type { ActionBinding, ActionThen, VisionEvent } from "../engines/types";
import {
  exportBindingsJson,
  loadBindings,
  removeGestureBinding,
  resetBindings,
  saveBindings,
  upsertGestureBinding,
} from "../actions/bindingsStore";
import {
  loadCustomGestures,
  type Point2,
} from "../actions/customGestures";
import {
  handleVisionEvent,
  onActionLog,
  setAppCommandHandler,
  type ActionLogEntry,
} from "../actions/actionBus";
import {
  ACTION_PRESETS,
  TEMPLATE_HINT,
  describeThen,
  presetToThen,
  thenToPreset,
  type ActionPresetId,
} from "../actions/actionCatalog";
import {
  deleteCustomOutput,
  loadCustomOutputs,
  newCustomOutputId,
  upsertCustomOutput,
  type CustomOutput,
} from "../actions/customOutputsStore";
import { CustomOutputBuilder } from "./CustomOutputBuilder";
import { AlwaysOnCard } from "./AlwaysOnCard";
import { TeachGestureCard } from "./TeachGestureCard";
import { VideoHandoffFields } from "./VideoHandoffFields";
import { KNOWN_GESTURES } from "../engines/gestureEngine";
import {
  acquireWakeLock,
  consumeAlwaysOnQuery,
  loadAlwaysOn,
  releaseWakeLock,
  saveAlwaysOn,
} from "../lib/alwaysOn";

import "../styles/forge-suite.css";
import "../styles/forge-chrome.css";

const SUITE_HOME = "https://forgesuiteui.com";

const CATALOG_CHIPS: { id: ConnectionTab; label: string; icon: ReactNode }[] = [
  { id: "production", label: "Production", icon: <PiCpuBold /> },
  { id: "hf", label: "HF models", icon: <PiHardDrivesBold /> },
  { id: "cloudflare", label: "Cloudflare", icon: <PiCloudBold /> },
  { id: "civitai", label: "CivitAI", icon: <PiImageBold /> },
  { id: "forge_endpoint", label: "Forge endpoints", icon: <PiCpuBold /> },
];

type ChatMsg = { role: "user" | "assistant"; text: string; imageUrl?: string };

export const ForgeChrome = memo(() => {
  const [shellMode, setShellMode] = useState<ShellMode>(() => loadShellMode());
  const { selectorOn, runtime } = useCatalogRuntime("visionforge");
  const showPicker = selectorOn === true;
  const pickEngine = pickEngineFromRuntime(runtime);
  const [endpoint, setEndpoint] = useState<VisionEndpoint | null>(() => loadEndpoint());
  const [user, setUser] = useState<SuiteUser | null>(null);
  const [wallet, setWallet] = useState<WalletSnapshot | null>(null);
  const [authBusy, setAuthBusy] = useState(false);
  const [showSkins, setShowSkins] = useState(false);
  const [themeId, setThemeId] = useState<ThemeId>(() => getStoredThemeId());
  const [fxId, setFxId] = useState(() => getBgFxId());
  const [pickerSkins, setPickerSkins] = useState(() => listPickerThemes(listThemes()));
  const [pickerMotions, setPickerMotions] = useState(() =>
    listPickerMotions(BG_FX_OPTIONS),
  );
  useEffect(() => {
    const sync = () => {
      setPickerSkins(listPickerThemes(listThemes()));
      setPickerMotions(listPickerMotions(BG_FX_OPTIONS));
    };
    window.addEventListener(SUITE_SKINS_EVENT, sync);
    const t = window.setTimeout(sync, 400);
    return () => {
      window.removeEventListener(SUITE_SKINS_EVENT, sync);
      window.clearTimeout(t);
    };
  }, []);
  const [showAbout, setShowAbout] = useState(false);
  /** usable-ui: tools rail collapsible; stage owns FOV. Prefer collapsed on narrow. */
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem("visionforge.sidebar.v1");
      if (stored === "1") return true;
      if (stored === "0") return false;
    } catch {
      /* ignore */
    }
    try {
      return window.matchMedia("(max-width: 900px)").matches;
    } catch {
      return false;
    }
  });
  const persistSidebar = (collapsed: boolean) => {
    setSidebarCollapsed(collapsed);
    try {
      localStorage.setItem("visionforge.sidebar.v1", collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  };

  // Live
  const [liveOn, setLiveOn] = useState(false);
  const [liveBusy, setLiveBusy] = useState(false);
  const [hudGesture, setHudGesture] = useState("—");
  const [hudConf, setHudConf] = useState(0);
  const [hudStatus, setHudStatus] = useState("idle");
  const [eventLog, setEventLog] = useState<string[]>([]);
  const [actionLog, setActionLog] = useState<ActionLogEntry[]>([]);
  const [bindings, setBindings] = useState<ActionBinding[]>(() => loadBindings());
  const [mappingsPaused, setMappingsPaused] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [stageFlash, setStageFlash] = useState(false);
  const [customOutputs, setCustomOutputs] = useState<CustomOutput[]>(() => loadCustomOutputs());
  const [draftName, setDraftName] = useState("My custom output");
  const [draftSteps, setDraftSteps] = useState<ActionThen[]>(() => [
    { kind: "toast", message: "{{gesture_label}} · {{confidence}}%" },
    { kind: "beep", frequency: 880 },
  ]);
  const [draftPausedOk, setDraftPausedOk] = useState(false);
  const [applyTarget, setApplyTarget] = useState<string>("");
  const [alwaysOn, setAlwaysOn] = useState(() => loadAlwaysOn());
  const [customGestures, setCustomGestures] = useState(() => loadCustomGestures());
  const alwaysOnRef = useRef(alwaysOn);
  const lastHandRef = useRef<Point2[] | null>(null);
  const autoStartedRef = useRef(false);

  // Ask
  const [askPrompt, setAskPrompt] = useState("Describe this image. Extract any visible text (OCR).");
  const [askBusy, setAskBusy] = useState(false);
  const [askError, setAskError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Suite BookForge inference engines (catalog UI = GpuBrowserMount)
  const [suiteSession, setSuiteSession] = useState<InferenceSession | null>(() => loadSavedSession());
  const [connectStatus, setConnectStatus] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const processVideoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const bindingsRef = useRef(bindings);
  const pausedRef = useRef(mappingsPaused);
  const liveOnRef = useRef(liveOn);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    bindingsRef.current = bindings;
  }, [bindings]);
  useEffect(() => {
    pausedRef.current = mappingsPaused;
  }, [mappingsPaused]);
  useEffect(() => {
    liveOnRef.current = liveOn;
  }, [liveOn]);
  useEffect(() => {
    alwaysOnRef.current = alwaysOn;
  }, [alwaysOn]);

  const pushLog = useCallback((line: string) => {
    setEventLog((prev) => [`${new Date().toLocaleTimeString()} ${line}`, ...prev].slice(0, 40));
  }, []);

  const showToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2200);
  }, []);

  const onVisionEvent = useCallback(
    (e: VisionEvent) => {
      if (e.type === "landmarks") {
        lastHandRef.current = e.points.map((p) => ({ x: p[0], y: p[1] }));
        return;
      }
      if (e.type === "gesture") {
        setHudGesture(e.name);
        setHudConf(e.confidence);
        pushLog(`gesture ${e.name} ${(e.confidence * 100).toFixed(0)}%`);
        void handleVisionEvent(e, bindingsRef.current, {
          mappingsPaused: pausedRef.current,
        });
      } else if (e.type === "status") {
        setHudStatus(e.message);
        if (e.level === "error" || e.level === "warn") pushLog(e.message);
      }
    },
    [pushLog],
  );

  useEffect(() => {
    return onActionLog((entry) => {
      setActionLog((prev) => [entry, ...prev].slice(0, 30));
      pushLog(entry.summary);
    });
  }, [pushLog]);

  // Toast events from action bus
  useEffect(() => {
    const onToast = (ev: Event) => {
      const d = (ev as CustomEvent).detail as { message?: string };
      if (d?.message) showToast(d.message);
    };
    window.addEventListener("visionforge-toast", onToast);
    return () => window.removeEventListener("visionforge-toast", onToast);
  }, [showToast]);

  // Legacy emit pause/mute still pauses mappings if someone has old bindings
  useEffect(() => {
    const onEmit = (ev: Event) => {
      const d = (ev as CustomEvent).detail as { channel?: string };
      if (d?.channel === "visionforge.pause" || d?.channel === "visionforge.mute") {
        setMappingsPaused(true);
        showToast("Mappings paused");
      }
    };
    window.addEventListener("visionforge-emit", onEmit);
    return () => window.removeEventListener("visionforge-emit", onEmit);
  }, [showToast]);

  const refreshAuth = useCallback(async () => {
    applyTokensFromHash();
    if (!isLoggedIn()) {
      setUser(null);
      setWallet(null);
      return;
    }
    try {
      const me = await fetchMe();
      setUser(me);
      const w = await fetchWallet();
      setWallet(w);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    consumeBinderQuery();
    const wantAlways = consumeAlwaysOnQuery();
    if (wantAlways) {
      setAlwaysOn(true);
      saveShellMode("live");
      setShellMode("live");
    }
    setEndpoint(loadEndpoint());
    setShellMode(loadShellMode());
    void refreshAuth();
  }, [refreshAuth]);

  // Escape closes skins strip, about modal, and (on narrow) tools drawer
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (showAbout) {
        setShowAbout(false);
        return;
      }
      if (showSkins) {
        setShowSkins(false);
        return;
      }
      if (!sidebarCollapsed && window.matchMedia("(max-width: 900px)").matches) {
        persistSidebar(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showAbout, showSkins, sidebarCollapsed]);

  // Stop live engine on unmount / shell mode leave
  useEffect(() => {
    return () => {
      try {
        getEngine("gesture")?.stop();
      } catch {
        /* ignore */
      }
    };
  }, []);

  const setShellPersist = (m: ShellMode) => {
    setShellMode(m);
    saveShellMode(m);
    if (m !== "live" && liveOn && !alwaysOnRef.current) {
      void stopLive();
    }
  };

  useEffect(() => {
    if (selectorOn === false && isCatalogShell(shellMode)) {
      setShellPersist("ask");
    }
  }, [selectorOn, shellMode]);

  const startLive = async () => {
    setLiveBusy(true);
    try {
      const eng = getEngine("gesture");
      if (!eng) throw new Error("Gesture engine missing from registry");
      await eng.start({
        videoEl: videoRef.current || undefined,
        processVideoEl: processVideoRef.current || undefined,
        canvasEl: canvasRef.current || undefined,
        endpoint,
        user: user ? { email: user.email } : undefined,
        onEvent: onVisionEvent,
        alwaysOn: alwaysOnRef.current,
      });
      setLiveOn(true);
      setMappingsPaused(false);
      pushLog("gesture engine started");
    } catch (e) {
      pushLog(e instanceof Error ? e.message : String(e));
      setHudStatus(e instanceof Error ? e.message : String(e));
    } finally {
      setLiveBusy(false);
    }
  };

  const stopLive = async () => {
    try {
      getEngine("gesture")?.stop();
    } catch {
      /* ignore */
    }
    setLiveOn(false);
    setHudGesture("—");
    setHudConf(0);
    setHudStatus("stopped");
    void releaseWakeLock();
  };

  const setAlwaysOnPersist = (on: boolean) => {
    setAlwaysOn(on);
    saveAlwaysOn(on);
    if (on) {
      saveShellMode("live");
      setShellMode("live");
      if (!liveOnRef.current) void startLive();
    } else {
      void releaseWakeLock();
    }
  };

  useEffect(() => {
    if (!alwaysOn || autoStartedRef.current) return;
    autoStartedRef.current = true;
    const t = window.setTimeout(() => {
      if (!liveOnRef.current) void startLive();
    }, 300);
    return () => window.clearTimeout(t);
    // mount / always-on restore only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alwaysOn]);

  useEffect(() => {
    if (!alwaysOn || !liveOn) {
      void releaseWakeLock();
      return;
    }
    void acquireWakeLock();
    const onVis = () => {
      if (document.visibilityState === "visible" && alwaysOnRef.current && liveOnRef.current) {
        void acquireWakeLock();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [alwaysOn, liveOn]);

  const grabFrame = async (): Promise<Blob | null> => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    const c = document.createElement("canvas");
    c.width = video.videoWidth;
    c.height = video.videoHeight;
    const ctx = c.getContext("2d");
    if (!ctx) return null;
    // un-mirror for ask
    ctx.translate(c.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    return new Promise((resolve) => c.toBlob((b) => resolve(b), "image/jpeg", 0.92));
  };

  const onImageFile = (file: File | null) => {
    if (!file) {
      setImageBlob(null);
      if (imagePreview) URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
      return;
    }
    setImageBlob(file);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(URL.createObjectURL(file));
  };

  const grabToAsk = async () => {
    const blob = await grabFrame();
    if (!blob) {
      setAskError("Start Live camera first, then grab a frame");
      showToast("Start camera first");
      return;
    }
    setImageBlob(blob);
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImagePreview(URL.createObjectURL(blob));
    setShellPersist("ask");
    pushLog("frame grabbed → ask");
    showToast("Frame → Ask");
  };

  const copyFrameToClipboard = async (): Promise<string> => {
    const blob = await grabFrame();
    if (!blob) throw new Error("No camera frame");
    if (!navigator.clipboard?.write) {
      throw new Error("Clipboard image API not available");
    }
    const item = new ClipboardItem({ [blob.type || "image/jpeg"]: blob });
    await navigator.clipboard.write([item]);
    showToast("Frame copied");
    return "copied frame";
  };

  const flashStage = () => {
    setStageFlash(true);
    window.setTimeout(() => setStageFlash(false), 280);
    showToast("Flash");
    return "flash";
  };

  // Shell commands for gesture app actions
  useEffect(() => {
    setAppCommandHandler(async (command) => {
      switch (command) {
        case "grab_to_ask": {
          await grabToAsk();
          return "grab → ask";
        }
        case "toggle_mappings": {
          setMappingsPaused((p) => {
            const next = !p;
            showToast(next ? "Mappings paused" : "Mappings live");
            return next;
          });
          return "toggle mappings";
        }
        case "toggle_camera": {
          if (liveOnRef.current) {
            await stopLive();
            showToast("Camera stopped");
            return "camera off";
          }
          await startLive();
          showToast("Camera started");
          return "camera on";
        }
        case "copy_frame":
          return await copyFrameToClipboard();
        case "flash_stage":
          return flashStage();
        default:
          return `unknown app:${command}`;
      }
    });
    return () => setAppCommandHandler(null);
    // Intentionally stable shell handlers; use refs for live state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  const runAsk = async () => {
    setAskError(null);
    setAskBusy(true);
    try {
      const eng = getEngine("vlm");
      if (!eng?.runJob) throw new Error("VLM engine missing");
      await eng.start({
        endpoint,
        user: user ? { email: user.email } : undefined,
        onEvent: onVisionEvent,
      });
      let blob = imageBlob;
      if (!blob && liveOn) blob = await grabFrame();
      if (!blob) throw new Error("Attach an image or grab a Live frame");

      const preview = imagePreview || URL.createObjectURL(blob);
      setMessages((m) => [
        ...m,
        { role: "user", text: askPrompt.trim(), imageUrl: preview },
      ]);

      const result = await eng.runJob({ image: blob, prompt: askPrompt.trim() });
      setMessages((m) => [...m, { role: "assistant", text: result.text }]);
      pushLog(`vlm ok · ${result.backend || suiteSession?.source || "catalog"}`);
      if (suiteSession) void refreshAuth();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setAskError(msg);
      pushLog(`vlm error · ${msg}`);
    } finally {
      setAskBusy(false);
    }
  };

  const updateBinding = (id: string, patch: Partial<ActionBinding>) => {
    setBindings((prev) => {
      const next = prev.map((b) => (b.id === id ? { ...b, ...patch } : b));
      saveBindings(next);
      return next;
    });
  };

  const setBindingPreset = (id: string, presetId: ActionPresetId) => {
    const preset = ACTION_PRESETS.find((p) => p.id === presetId);
    const existing = bindings.find((b) => b.id === id);
    const url =
      existing?.then.kind === "open_url" ? existing.then.url : "https://forgesuiteui.com";
    const keys = existing?.then.kind === "hotkey" ? existing.then.keys : "Enter";
    let text: string | undefined;
    if (existing?.then.kind === "toast") text = existing.then.message;
    if (existing?.then.kind === "speak") text = existing.then.text;
    if (existing?.then.kind === "copy_text") text = existing.then.text;
    if (existing?.then.kind === "forge_handoff") text = existing.then.params?.prompt;

    if (presetId === "custom_ref") {
      const first = customOutputs[0];
      updateBinding(id, {
        then: first
          ? { kind: "custom_ref", outputId: first.id }
          : presetToThen("custom"),
        fireWhenPaused: first?.fireWhenPaused || false,
      });
      return;
    }

    updateBinding(id, {
      then: presetToThen(presetId, { url, keys, text }),
      fireWhenPaused: !!preset?.fireWhenPaused,
    });
  };

  const updateSequenceSteps = (bindingId: string, steps: ActionThen[], label?: string) => {
    const existing = bindings.find((b) => b.id === bindingId);
    const prevLabel =
      existing?.then.kind === "sequence" ? existing.then.label : "Custom";
    updateBinding(bindingId, {
      then: { kind: "sequence", steps, label: label ?? prevLabel ?? "Custom" },
    });
  };

  const saveDraftRecipe = () => {
    const name = draftName.trim() || "Custom output";
    if (!draftSteps.length) {
      showToast("Add at least one step");
      return;
    }
    const recipe: CustomOutput = {
      id: newCustomOutputId(),
      name,
      steps: draftSteps,
      fireWhenPaused: draftPausedOk,
      updatedAt: Date.now(),
    };
    setCustomOutputs(upsertCustomOutput(recipe));
    showToast(`Saved “${name}”`);
  };

  const applyRecipeToGesture = (outputId: string, bindingId: string) => {
    if (!bindingId || !outputId) return;
    const recipe = customOutputs.find((o) => o.id === outputId);
    updateBinding(bindingId, {
      then: { kind: "custom_ref", outputId },
      fireWhenPaused: !!recipe?.fireWhenPaused,
      enabled: true,
    });
    showToast(`Applied to ${bindings.find((b) => b.id === bindingId)?.when.gesture || "gesture"}`);
  };

  const downloadBindings = () => {
    const json = exportBindingsJson(bindings);
    const blob = new Blob([json], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "visionforge-bindings.json";
    a.click();
  };

  const catalogTab = isCatalogShell(shellMode);
  const costHint =
    shellMode === "live"
      ? "Live hands = free in-browser. No suite debit."
      : shellMode === "screen"
        ? "Screen mode (when available) uses a connected catalog model for heavy inference."
        : catalogTab
          ? suiteSession
            ? `${suiteSession.model.name} connected · ${suiteSession.source || "catalog"}`
            : "Connect a model, then use Ask for vision Q&A."
          : suiteSession
            ? `${suiteSession.model.name} · debit client visionforge`
            : "Connect a model from HF, Cloudflare, CivitAI, or Forge endpoints before asking";

  const engines = listEngines();

  return (
    <div className="vf-shell-fill">
      <BackgroundFx />
      <header className="vf-chrome" role="banner">
        <div className="vf-chrome-brand">
          <span className="vf-mark" aria-hidden>
            <PiEyeBold />
          </span>
          <div className="vf-brand-text">
            <strong>VisionForge</strong>
            <span>Live · ask · engines</span>
          </div>
        </div>

        <div className="vf-mode-strip" role="tablist" aria-label="Product mode">
          {(
            [
              { id: "live" as const, label: "Live", icon: <PiWebcamBold /> },
              { id: "ask" as const, label: "Ask", icon: <PiImageBold /> },
              ...(showPicker ? CATALOG_CHIPS : []),
              { id: "screen" as const, label: "Screen", icon: <PiMonitorBold />, beta: true },
            ]
          ).map((m) => (
            <button
              key={m.id}
              type="button"
              role="tab"
              aria-selected={shellMode === m.id}
              className={`vf-mode-btn${shellMode === m.id ? " vf-mode-btn--on" : ""}${
                "beta" in m && m.beta ? " vf-mode-btn--beta" : ""
              }`}
              onClick={() => setShellPersist(m.id)}
            >
              {m.icon} <span className="vf-mode-label">{m.label}</span>
              {"beta" in m && m.beta ? " β" : ""}
            </button>
          ))}
        </div>

        <div className="vf-chrome-actions">
          <button type="button" className="vf-chip" onClick={() => setShowSkins((v) => !v)}>
            Skin / Motion
          </button>
          <button type="button" className="vf-chip" onClick={() => setShowAbout(true)}>
            About
          </button>
          {wallet != null && isLoggedIn() && (
            <span className="vf-wallet" title="Suite wallet (shared)">
              <PiWalletBold /> ${wallet.balanceUsd.toFixed(2)}
              {wallet.subscriptionActive ? " · Member" : ""}
            </span>
          )}
          {isLoggedIn() && user ? (
            <button
              type="button"
              className="vf-chip"
              onClick={() => {
                clearSession();
                setUser(null);
                setWallet(null);
              }}
              title={user.email}
            >
              <PiSignOutBold /> {user.email.split("@")[0]}
            </button>
          ) : (
            <button
              type="button"
              className="vf-btn-primary"
              disabled={authBusy}
              onClick={() => {
                setAuthBusy(true);
                startGoogleLoginWeb();
              }}
            >
              <PiGoogleLogoBold /> Google
            </button>
          )}
        </div>
      </header>

      {showSkins && (
        <div className="vf-panel">
          <div className="vf-panel-title">Skins</div>
          <div className="vf-skin-row">
            {pickerSkins.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`vf-skin-chip${themeId === t.id ? " vf-skin-chip--on" : ""}`}
                title={t.description}
                onClick={() => {
                  selectTheme(t.id);
                  setThemeId(t.id);
                }}
              >
                <span className="vf-skin-swatches">
                  {t.preview.map((c, i) => (
                    <i key={i} style={{ background: c }} />
                  ))}
                </span>
                {t.name}
              </button>
            ))}
          </div>
          <div className="vf-panel-title" style={{ marginTop: 10 }}>
            Motion
          </div>
          <div className="vf-skin-row">
            {pickerMotions.map((o) => (
              <button
                key={o.id}
                type="button"
                className={`vf-skin-chip${fxId === o.id ? " vf-skin-chip--on" : ""}`}
                title={o.blurb}
                onClick={() => {
                  setBgFxId(o.id);
                  setFxId(o.id);
                }}
              >
                {o.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="vf-studio">
        {sidebarCollapsed ? (
          <button
            type="button"
            className="vf-rail-collapsed"
            title="Show engines & bindings"
            aria-expanded={false}
            onClick={() => persistSidebar(false)}
          >
            <span>»</span>
            <span className="vf-rail-label">Tools</span>
          </button>
        ) : (
          <>
            <button
              type="button"
              className="vf-sidebar-backdrop"
              aria-label="Close tools"
              onClick={() => persistSidebar(true)}
            />
            <aside className="vf-sidebar" aria-label="Tools">
              <div className="vf-sidebar-head">
                <span className="vf-label" style={{ margin: 0 }}>
                  Tools
                </span>
                <button
                  type="button"
                  className="vf-chip"
                  title="Collapse tools rail"
                  aria-expanded={true}
                  onClick={() => persistSidebar(true)}
                >
                  «
                </button>
              </div>
          <section className="vf-card">
            <div className="vf-label">Engines</div>
            <div className="vf-engine-pick">
              {engines.map((eng) => (
                <button
                  key={eng.id}
                  type="button"
                  className={`vf-engine-btn${
                    (shellMode === "live" && eng.id === "gesture") ||
                    (shellMode === "ask" && eng.id === "vlm") ||
                    (shellMode === "screen" && eng.id === "screen")
                      ? " vf-engine-btn--on"
                      : ""
                  }`}
                  onClick={() => {
                    if (eng.id === "gesture") setShellPersist("live");
                    else if (eng.id === "vlm") setShellPersist("ask");
                    else if (eng.id === "screen") setShellPersist("screen");
                  }}
                >
                  {eng.label}
                  {eng.status === "coming-soon" ? " · soon" : eng.status === "beta" ? " · beta" : ""}
                  {eng.description ? <small>{eng.description}</small> : null}
                </button>
              ))}
            </div>
            <div className="vf-cost-hint">{costHint}</div>
            {showPicker && (
            <div className="vf-row" style={{ marginTop: 4 }}>
              {CATALOG_CHIPS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`vf-chip${shellMode === c.id ? " vf-chip--warn" : ""}`}
                  onClick={() => setShellPersist(c.id)}
                >
                  {c.label}
                </button>
              ))}
            </div>
            )}
          </section>

          {(catalogTab || shellMode === "ask") && (
            <section className="vf-card">
              <div className="vf-label">Suite session</div>
              {suiteSession ? (
                <>
                  <div className="vf-success">Connected · {suiteSession.model.name}</div>
                  <div className="vf-row">
                    <button
                      type="button"
                      className="vf-chip"
                      onClick={() => {
                        void stopSuiteSession(suiteSession.instance.id);
                        setSuiteSession(null);
                        setConnectStatus(null);
                      }}
                    >
                      Disconnect
                    </button>
                    {shellMode !== "ask" && (
                      <button
                        type="button"
                        className="vf-btn-primary"
                        onClick={() => setShellPersist("ask")}
                      >
                        Open Ask
                      </button>
                    )}
                  </div>
                </>
              ) : (
                <div className="vf-cost-hint">
                  {shellMode === "ask"
                    ? "No engine connected — use HF, Cloudflare, CivitAI, or Forge endpoints."
                    : "Browse the catalog in the main pane to connect."}
                </div>
              )}
              {connectStatus && !suiteSession && (
                <div className="vf-success">{connectStatus}</div>
              )}
            </section>
          )}

          {shellMode === "live" && (
            <>
            <AlwaysOnCard
              alwaysOn={alwaysOn}
              liveOn={liveOn}
              onToggle={setAlwaysOnPersist}
            />
            <TeachGestureCard
              liveOn={liveOn}
              lastHandRef={lastHandRef}
              gestures={customGestures}
              onChange={setCustomGestures}
              onTaught={(gName) => {
                setBindings((prev) => {
                  const next = upsertGestureBinding(prev, gName);
                  saveBindings(next);
                  return next;
                });
                showToast(`Taught ${gName.replace(/_/g, " ")}`);
              }}
              onDeleted={(gName) => {
                setBindings((prev) => {
                  const next = removeGestureBinding(prev, gName);
                  saveBindings(next);
                  return next;
                });
                showToast(`Removed ${gName.replace(/_/g, " ")}`);
              }}
            />
            <section className="vf-card">
              <div className="vf-label">Gesture actions</div>
              <div className="vf-cost-hint" style={{ marginBottom: 8 }}>
                Pick what each gesture does in this tab. Free · no download.
              </div>
              <div className="vf-row">
                <button
                  type="button"
                  className={`vf-chip${mappingsPaused ? " vf-chip--warn" : ""}`}
                  onClick={() => {
                    setMappingsPaused((p) => !p);
                    showToast(mappingsPaused ? "Mappings live" : "Mappings paused");
                  }}
                >
                  {mappingsPaused ? "Resume mappings" : "Pause mappings"}
                </button>
                <button
                  type="button"
                  className="vf-chip"
                  onClick={() => {
                    setBindings(resetBindings());
                    showToast("Defaults restored");
                  }}
                >
                  Reset defaults
                </button>
                <button type="button" className="vf-chip" onClick={downloadBindings}>
                  Export JSON
                </button>
              </div>
              {mappingsPaused && (
                <div className="vf-beta-banner" style={{ marginTop: 8 }}>
                  <strong>Mappings paused</strong>
                  Fist (default) or Resume to fire actions again.
                </div>
              )}
              <div className="vf-bindings">
                {bindings.map((b) => {
                  const presetId = thenToPreset(b.then);
                  const preset = ACTION_PRESETS.find((p) => p.id === presetId);
                  const forgeHandoff =
                    b.then.kind === "forge_handoff" &&
                    (b.then.product === "videoforge" || b.then.product === "imageforge")
                      ? b.then
                      : null;
                  const selectValue =
                    presetId === "custom_ref" && b.then.kind === "custom_ref"
                      ? `ref:${b.then.outputId}`
                      : presetId;
                  return (
                    <div key={b.id} className="vf-binding">
                      <input
                        type="checkbox"
                        title="Enabled"
                        checked={b.enabled}
                        onChange={(e) => updateBinding(b.id, { enabled: e.target.checked })}
                      />
                      <div className="vf-binding-meta">
                        <div className="vf-binding-gesture">
                          {customGestures.some((g) => g.name === b.when.gesture)
                            ? "★ "
                            : ""}
                          {(b.when.gesture || b.when.event || "event").replace(/_/g, " ")}
                        </div>
                        <select
                          className="vf-select vf-binding-select"
                          value={selectValue}
                          disabled={!b.enabled}
                          onChange={(e) => {
                            const v = e.target.value;
                            if (v.startsWith("ref:")) {
                              const outputId = v.slice(4);
                              const recipe = customOutputs.find((o) => o.id === outputId);
                              updateBinding(b.id, {
                                then: { kind: "custom_ref", outputId },
                                fireWhenPaused: !!recipe?.fireWhenPaused,
                              });
                              return;
                            }
                            setBindingPreset(b.id, v as ActionPresetId);
                          }}
                          aria-label={`Action for ${b.when.gesture}`}
                        >
                          {ACTION_PRESETS.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.label}
                            </option>
                          ))}
                          {customOutputs.length > 0 && (
                            <optgroup label="Saved custom outputs">
                              {customOutputs.map((o) => (
                                <option key={o.id} value={`ref:${o.id}`}>
                                  ★ {o.name}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                        {preset?.hint && presetId !== "custom" && presetId !== "custom_ref" && (
                          <div className="vf-binding-hint">{preset.hint}</div>
                        )}
                        {b.then.kind === "custom_ref" && (
                          <div className="vf-binding-hint">
                            Recipe:{" "}
                            {customOutputs.find(
                              (o) =>
                                o.id ===
                                (b.then.kind === "custom_ref" ? b.then.outputId : ""),
                            )?.name || "missing — rebuild below"}
                          </div>
                        )}
                        {(b.then.kind === "toast" ||
                          b.then.kind === "speak" ||
                          b.then.kind === "copy_text") && (
                          <>
                            <input
                              className="vf-input vf-binding-param"
                              placeholder="Text · {{gesture_label}}"
                              value={
                                b.then.kind === "toast"
                                  ? b.then.message || ""
                                  : b.then.text || ""
                              }
                              onChange={(e) => {
                                const val = e.target.value;
                                if (b.then.kind === "toast") {
                                  updateBinding(b.id, {
                                    then: { kind: "toast", message: val },
                                  });
                                } else if (b.then.kind === "speak") {
                                  updateBinding(b.id, {
                                    then: { kind: "speak", text: val },
                                  });
                                } else {
                                  updateBinding(b.id, {
                                    then: { kind: "copy_text", text: val },
                                  });
                                }
                              }}
                            />
                            <div className="vf-binding-hint">{TEMPLATE_HINT}</div>
                          </>
                        )}
                        {b.then.kind === "open_url" && (
                          <input
                            className="vf-input vf-binding-param"
                            type="url"
                            placeholder="https://… · {{gesture}}"
                            value={b.then.url}
                            onChange={(e) =>
                              updateBinding(b.id, {
                                then: { kind: "open_url", url: e.target.value },
                              })
                            }
                          />
                        )}
                        {forgeHandoff && (
                          <>
                            <textarea
                              className="vf-input vf-binding-param"
                              rows={2}
                              placeholder={
                                forgeHandoff.product === "imageforge"
                                  ? "Image prompt · {{gesture_label}}"
                                  : "Video prompt · {{gesture_label}}"
                              }
                              value={forgeHandoff.params?.prompt || ""}
                              onChange={(e) =>
                                updateBinding(b.id, {
                                  then: {
                                    kind: "forge_handoff",
                                    product: forgeHandoff.product,
                                    params: {
                                      ...forgeHandoff.params,
                                      prompt: e.target.value,
                                      autogen: forgeHandoff.params?.autogen ?? "1",
                                    },
                                  },
                                })
                              }
                            />
                            <label className="vf-binding" style={{ cursor: "pointer", marginTop: 4 }}>
                              <input
                                type="checkbox"
                                checked={(forgeHandoff.params?.autogen ?? "1") !== "0"}
                                onChange={(e) =>
                                  updateBinding(b.id, {
                                    then: {
                                      kind: "forge_handoff",
                                      product: forgeHandoff.product,
                                      params: {
                                        ...forgeHandoff.params,
                                        autogen: e.target.checked ? "1" : "0",
                                      },
                                    },
                                  })
                                }
                              />
                              <div className="vf-binding-meta">
                                <div className="vf-binding-hint">
                                  Start generate when{" "}
                                  {forgeHandoff.product === "imageforge" ? "ImageForge" : "VideoForge"}{" "}
                                  opens (suite credits)
                                </div>
                              </div>
                            </label>
                            <VideoHandoffFields
                              purpose={forgeHandoff.product === "imageforge" ? "image" : "video"}
                              params={forgeHandoff.params || {}}
                              wallet={wallet}
                              onChange={(params) =>
                                updateBinding(b.id, {
                                  then: {
                                    kind: "forge_handoff",
                                    product: forgeHandoff.product,
                                    params: {
                                      ...forgeHandoff.params,
                                      ...params,
                                    },
                                  },
                                })
                              }
                            />
                            <div className="vf-binding-hint">{TEMPLATE_HINT}</div>
                          </>
                        )}
                        {b.then.kind === "hotkey" && (
                          <input
                            className="vf-input vf-binding-param"
                            placeholder="Enter, ArrowUp, Ctrl+S…"
                            value={b.then.keys}
                            onChange={(e) =>
                              updateBinding(b.id, {
                                then: { kind: "hotkey", keys: e.target.value },
                              })
                            }
                          />
                        )}
                        {b.then.kind === "sequence" && (
                          <CustomOutputBuilder
                            compact
                            showLabel
                            wallet={wallet}
                            label={b.then.label || "Custom"}
                            steps={b.then.steps}
                            onLabelChange={(label) =>
                              updateSequenceSteps(b.id, b.then.kind === "sequence" ? b.then.steps : [], label)
                            }
                            onChange={(steps) => updateSequenceSteps(b.id, steps)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="vf-cost-hint">
                Built-in: {KNOWN_GESTURES.filter((g) => g !== "None").join(", ")}
                {customGestures.length
                  ? ` · Taught: ${customGestures.map((g) => g.name).join(", ")}`
                  : ""}
              </div>
            </section>
            </>
          )}

          {shellMode === "live" && (
            <section className="vf-card">
              <div className="vf-label">Build your own output</div>
              <div className="vf-cost-hint" style={{ marginBottom: 8 }}>
                Stack steps (toast → beep → open URL…). Save as a recipe and assign to any
                gesture. Free · in-browser only.
              </div>
              <input
                className="vf-input"
                style={{ width: "100%", marginBottom: 8 }}
                placeholder="Recipe name"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
              />
              <CustomOutputBuilder steps={draftSteps} onChange={setDraftSteps} wallet={wallet} />
              <label className="vf-binding" style={{ marginTop: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={draftPausedOk}
                  onChange={(e) => setDraftPausedOk(e.target.checked)}
                />
                <div className="vf-binding-meta">
                  Fire even when mappings are paused
                </div>
              </label>
              <div className="vf-row" style={{ marginTop: 8 }}>
                <button type="button" className="vf-btn-primary" onClick={saveDraftRecipe}>
                  Save recipe
                </button>
                <button
                  type="button"
                  className="vf-chip"
                  onClick={() => {
                    setDraftSteps([
                      { kind: "toast", message: "{{gesture_label}} · {{confidence}}%" },
                      { kind: "beep", frequency: 880 },
                    ]);
                    setDraftName("My custom output");
                    setDraftPausedOk(false);
                  }}
                >
                  Clear draft
                </button>
              </div>

              {customOutputs.length > 0 && (
                <>
                  <div className="vf-label" style={{ marginTop: 14 }}>
                    Saved recipes
                  </div>
                  <div className="vf-custom-list">
                    {customOutputs.map((o) => (
                      <div key={o.id} className="vf-custom-recipe">
                        <div>
                          <strong>{o.name}</strong>
                          <div className="vf-binding-hint">
                            {o.steps.length} step{o.steps.length === 1 ? "" : "s"}
                            {o.fireWhenPaused ? " · works while paused" : ""}
                          </div>
                          <div className="vf-binding-hint">
                            {o.steps.map((s) => describeThen(s)).join(" → ")}
                          </div>
                        </div>
                        <div className="vf-row">
                          <select
                            className="vf-select vf-binding-select"
                            value={applyTarget}
                            onChange={(e) => setApplyTarget(e.target.value)}
                            aria-label="Assign recipe to gesture"
                          >
                            <option value="">Assign to…</option>
                            {bindings.map((b) => (
                              <option key={b.id} value={b.id}>
                                {(b.when.gesture || "event").replace(/_/g, " ")}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="vf-chip"
                            disabled={!applyTarget}
                            onClick={() => applyRecipeToGesture(o.id, applyTarget)}
                          >
                            Apply
                          </button>
                          <button
                            type="button"
                            className="vf-chip"
                            onClick={() => {
                              setDraftName(o.name);
                              setDraftSteps(o.steps.map((s) => ({ ...s })));
                              setDraftPausedOk(!!o.fireWhenPaused);
                              showToast("Loaded into editor");
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="vf-chip"
                            onClick={() => {
                              setCustomOutputs(deleteCustomOutput(o.id));
                              showToast("Recipe deleted");
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </section>
          )}

              <section className="vf-card">
                <div className="vf-label">Event log</div>
                <div className="vf-log">
                  {eventLog.length === 0 ? (
                    <span className="vf-cost-hint">Events appear here…</span>
                  ) : (
                    eventLog.map((l, i) => (
                      <div key={i} className="vf-log-line">
                        {l}
                      </div>
                    ))
                  )}
                </div>
              </section>
            </aside>
          </>
        )}

        <main className="vf-main">
          <video
            ref={processVideoRef}
            className="vf-process-video"
            playsInline
            muted
            autoPlay
            aria-hidden
          />
          {toast && (
            <div className="vf-toast" role="status">
              {toast}
            </div>
          )}

          {shellMode === "live" && (
            <div className="vf-main-head">
              <h1 className="vf-studio-title">Live hands</h1>
              <span className="vf-studio-sub">
                {alwaysOn ? "Always on · gestures → actions" : "Gestures → tools actions"}
              </span>
            </div>
          )}

          <div
            ref={stageRef}
            className={
              shellMode === "live"
                ? `vf-stage${stageFlash ? " vf-stage--flash" : ""}`
                : alwaysOn
                  ? "vf-always-pip"
                  : "vf-stage-park"
            }
            hidden={shellMode !== "live" && !alwaysOn}
            onClick={() => {
              if (shellMode !== "live" && alwaysOn) setShellPersist("live");
            }}
            title={
              shellMode !== "live" && alwaysOn
                ? "Always on — click to return to Live"
                : undefined
            }
          >
            <video ref={videoRef} playsInline muted />
            <canvas ref={canvasRef} />
            <div className="vf-hud">
              <span className="vf-hud-pill">{hudGesture.replace(/_/g, " ")}</span>
              <span className="vf-hud-pill">
                {hudConf ? `${(hudConf * 100).toFixed(0)}%` : "—"}
              </span>
              <span className="vf-hud-pill">{hudStatus}</span>
              {alwaysOn && liveOn && (
                <span className="vf-hud-pill vf-hud-pill--on">ALWAYS ON</span>
              )}
              {mappingsPaused && (
                <span className="vf-hud-pill vf-hud-pill--warn">MAPPINGS PAUSED</span>
              )}
            </div>
          </div>

          {shellMode === "live" && (
              <div className="vf-main-foot">
                <div className="vf-row">
                  {!liveOn ? (
                    <button
                      type="button"
                      className="vf-btn-primary"
                      disabled={liveBusy}
                      onClick={() => void startLive()}
                    >
                      {liveBusy ? (
                        <>
                          <PiCircleNotchBold className="vf-spin" /> Starting…
                        </>
                      ) : (
                        <>
                          <PiWebcamBold /> Start camera
                        </>
                      )}
                    </button>
                  ) : (
                    <button type="button" className="vf-chip" onClick={() => void stopLive()}>
                      Stop camera
                    </button>
                  )}
                  <button type="button" className="vf-chip" onClick={() => void grabToAsk()}>
                    Grab → Ask
                  </button>
                  {mappingsPaused && (
                    <button
                      type="button"
                      className="vf-btn-primary"
                      onClick={() => {
                        setMappingsPaused(false);
                        showToast("Mappings live");
                      }}
                    >
                      Resume mappings
                    </button>
                  )}
                  <button
                    type="button"
                    className={`vf-chip${alwaysOn ? " vf-chip--warn" : ""}`}
                    onClick={() => setAlwaysOnPersist(!alwaysOn)}
                  >
                    {alwaysOn ? "Always on · on" : "Always on"}
                  </button>
                </div>
                {liveOn && (
                  <div className="vf-cost-hint">
                    Palm = toast · fist = pause · victory = grab · pinch = beep · thumbs up =
                    speak. Map a gesture to Open VideoForge to generate.
                  </div>
                )}
                {actionLog.length > 0 && (
                  <section className="vf-card vf-card--compact-log">
                    <div className="vf-label">Action bus</div>
                    <div className="vf-log">
                      {actionLog.map((a) => (
                        <div
                          key={a.id}
                          className={`vf-log-line${a.ok ? " vf-log-line--ok" : " vf-log-line--err"}`}
                        >
                          {new Date(a.t).toLocaleTimeString()} · {a.summary}
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
          )}

          {shellMode === "ask" && (
            <>
              <div className="vf-main-head">
                <h1 className="vf-studio-title">Ask</h1>
                <span className="vf-studio-sub">Image → catalog VLM</span>
              </div>
              {!suiteSession && showPicker && (
                <div className="vf-beta-banner">
                  <strong>Connect a vision engine</strong>
                  Open{" "}
                  {CATALOG_CHIPS.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="vf-chip"
                      style={{ verticalAlign: "middle", margin: "0 4px" }}
                      onClick={() => setShellPersist(c.id)}
                    >
                      {c.label}
                    </button>
                  ))}
                  pick a model, Connect.
                </div>
              )}
              {selectorOn === false && pickEngine && (
                <div className="vf-beta-banner">
                  AccessHub Pick · {pickEngine.name}
                </div>
              )}
              <section className="vf-card">
                <div className="vf-label">Image</div>
                <div className="vf-row">
                  <input
                    ref={fileRef}
                    className="vf-file"
                    type="file"
                    accept="image/*"
                    onChange={(e) => onImageFile(e.target.files?.[0] || null)}
                  />
                  {imagePreview && (
                    <img className="vf-thumb" src={imagePreview} alt="Ask attachment" />
                  )}
                  {imagePreview && (
                    <button
                      type="button"
                      className="vf-chip"
                      onClick={() => {
                        onImageFile(null);
                        if (fileRef.current) fileRef.current.value = "";
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="vf-label">Prompt</div>
                <textarea
                  className="vf-textarea"
                  value={askPrompt}
                  onChange={(e) => setAskPrompt(e.target.value)}
                  placeholder="What do you see? OCR? UI elements?"
                />
                <div className="vf-row">
                  <button
                    type="button"
                    className="vf-btn-primary"
                    disabled={askBusy || !askPrompt.trim()}
                    onClick={() => void runAsk()}
                  >
                    {askBusy ? (
                      <>
                        <PiCircleNotchBold className="vf-spin" /> Asking…
                      </>
                    ) : (
                      "Ask"
                    )}
                  </button>
                  <span className="vf-cost-hint">{costHint}</span>
                </div>
                {askError && <div className="vf-error">{askError}</div>}
              </section>
              <section className="vf-card vf-card--grow">
                <div className="vf-label">Messages</div>
                <div className="vf-chat">
                  {messages.length === 0 ? (
                    <span className="vf-cost-hint">
                      Attach an image or grab a Live frame, then ask.
                    </span>
                  ) : (
                    messages.map((m, i) => (
                      <div
                        key={i}
                        className={`vf-msg vf-msg--${m.role === "user" ? "user" : "assistant"}`}
                      >
                        {m.imageUrl && (
                          <img
                            className="vf-thumb"
                            src={m.imageUrl}
                            alt=""
                            style={{ display: "block", marginBottom: 6 }}
                          />
                        )}
                        {m.text}
                      </div>
                    ))
                  )}
                </div>
              </section>
            </>
          )}

          {catalogTab && showPicker && (
            <>
              <div className="vf-main-head">
                <h1 className="vf-studio-title">
                  {CATALOG_CHIPS.find((c) => c.id === shellMode)?.label || "Catalog"}
                </h1>
                <span className="vf-studio-sub">Connect a vision model for Ask</span>
              </div>
              <div className="vf-gpu-workspace">
                <GpuBrowserMount
                  tab={shellMode}
                  fill
                  tool="ask-vlm"
                  wallet={wallet}
                  session={suiteSession}
                  onStatus={(msg) => setConnectStatus(msg)}
                  onSession={(s) => {
                    setSuiteSession(s);
                    if (s) {
                      const ep: VisionEndpoint = {
                        bindKind: "openai-vision",
                        endpointUrl: s.baseUrl,
                        source: s.source && s.source !== "custom" ? s.source : "custom",
                        label: s.model.name,
                        model: s.modelId,
                        apiKey: s.apiKey,
                      };
                      saveEndpoint(ep);
                      setEndpoint(ep);
                      pushLog(`catalog · ${s.model.name}`);
                      showToast(`Connected · ${s.model.name}`);
                    }
                  }}
                />
              </div>
            </>
          )}

          {shellMode === "screen" && (
            <div className="vf-main-scroll">
              <div className="vf-main-head">
                <h1 className="vf-studio-title">Screen · soon</h1>
              </div>
              <div className="vf-beta-banner">
                <strong>Coming soon</strong>
                Needs a desktop companion and an explicit trust step. Live and Ask are ready today.
              </div>
              <section className="vf-card">
                <div className="vf-label">Safety</div>
                <p className="vf-studio-sub">
                  Live hands never control your OS. Screen automation will not ship without consent
                  UX and clear suite billing.
                </p>
              </section>
            </div>
          )}
        </main>
      </div>

      {showAbout && (
        <div className="vf-modal-backdrop" role="dialog" onClick={() => setShowAbout(false)}>
          <div className="vf-modal" onClick={(e) => e.stopPropagation()}>
            <h2>About VisionForge</h2>
            <p>
              <strong>VisionForge</strong> is a Forge Suite product: live vision that acts, plus
              ask-mode vision Q&amp;A on catalog engines (HF, Cloudflare, CivitAI, Forge endpoints).
              Not image gen (ImageForge), video gen (VideoForge), or voice (VoiceForge).
            </p>
            <p>
              <strong>Live</strong> — free in-browser hand tracking + action bindings.
              <br />
              <strong>Ask</strong> — image Q&amp;A after you connect a catalog model.
              <br />
              <strong>HF / Cloudflare / CivitAI / Forge endpoints</strong> — connect a vision
              model, then Ask.
              <br />
              <strong>Screen</strong> — reserved for a future trusted desktop companion.
            </p>
            <p>
              Shared suite Google login and USD wallet. App data shelf:{" "}
              <code>{VISIONFORGE_HF_BUCKET}</code>
            </p>
            <p>
              Member unlock: ${SUITE_MEMBER_PRICE_USD}/mo ·{" "}
              <a href={SUITE_HOME} target="_blank" rel="noreferrer">
                forgesuiteui.com
              </a>
            </p>
            <p className="vf-cost-hint" style={{ fontSize: 11 }}>
              Third-party runtimes used under the hood are attributed in NOTICE (not the product face).
            </p>
            {isLoggedIn() && (
              <div className="vf-wallet-actions">
                <button
                  type="button"
                  className="vf-chip"
                  onClick={() => void startCheckout(10).then((u) => u && (window.location.href = u))}
                >
                  Add $10 credits
                </button>
                <button
                  type="button"
                  className="vf-chip"
                  onClick={() =>
                    void subscribeMember().then((u) => u && (window.location.href = u))
                  }
                >
                  Become Member
                </button>
              </div>
            )}
            <button type="button" className="vf-btn-primary" onClick={() => setShowAbout(false)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

ForgeChrome.displayName = "ForgeChrome";
